import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi } from '@/shared/api';

/**
 * „Nächte freigeben" — die ausdrückliche Gegenrichtung zur Wiedervorlage: unsere Wahrheit sagt
 * „keine Buchung, also frei". Erst nach der Bestätigung im Dialog. Ohne Antwort ist offen, ob der
 * Server freigegeben hat; der Dialog bleibt dann offen und die Liste lädt neu.
 */
export function useReleaseNights() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<string | null>(null);

  const release = useMutation({
    mutationFn: (id: string) => api.channex.rederiveQuarantinedAvailability(id),
    // Offline pausiert TanStack sonst still, und „Nächte freigeben" dreht sich ohne Ende.
    networkMode: 'always',
    onSuccess: async (result) => {
      setTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['channex'] });
      toast.success(
        result.enqueued > 0
          ? `Verfügbarkeit für ${result.from} bis ${result.to} neu abgeleitet (${result.enqueued} Push${result.enqueued === 1 ? '' : 'es'}).`
          : 'Nichts einzureihen — die betroffenen Apartments nehmen keine ARI-Operationen mehr an.',
      );
    },
    onError: async (err) => {
      const apiError = await toApiError(err);
      toast.error(
        apiError.status === 0
          ? 'Keine Antwort vom Server – ob die Nächte freigegeben wurden, ist offen. Die Liste wird neu geladen.'
          : (apiError.detail ?? apiError.title),
      );
      await queryClient.invalidateQueries({ queryKey: ['channex'] });
    },
  });

  return {
    target,
    open: setTarget,
    close: () => setTarget(null),
    confirm: (id: string) => release.mutate(id),
    pending: release.isPending,
  };
}
